import React from "react";
import { Paragraph, InfoIcon, EfficiencyTag, ResponsiveImage, DescriptionList, ProductTable } from "@vwfs-bronson/bronson-react";
import { DurationUnit } from "@bus/models/dist/enums/new/duration-unit.enum";
import { Output } from "../../Text";
import { formatEuroAmount, formatMileage } from "../../functional/normalize";
import { Calculation } from "../../common/types/calculation";
import { VehicleData } from "../../common/types/vehicleData";
import { AdditionalServicesDescriptionItem } from "../../common/types/additionalServicesDescriptionItem";
import { notFalsy } from '../../utils';
import { TestProcedureType } from '../../common/types/vehicleModelEconomics';
import { getEfficiencyClassImage, parseEfficiencyClass } from './helper';
import DescriptionListGroup from '../../common/components/DescriptionListGroup/DescriptionListGroup';
import ENVKVShoppingCartCaption from '../../common/components/ENVKV/ENVKVShoppingCartCaption';
import { isENVKVNew } from '../../common/JourneyType';
import { Route as AppRoute } from "../../common/Navigation";

class ShoppingCart extends React.Component<
  ShoppingCartProps,
  ShoppingCartState
> {
  history: any;
  calculation: Calculation;
  vehicleData: VehicleData;
  additionalServices: AdditionalServicesDescriptionItem;
  durationUnit: string;
  currency: string;
  prefill: any;

  constructor(shoppingCartProps: ShoppingCartProps) {
    super(shoppingCartProps);

    this.history = shoppingCartProps.history;
    this.calculation = shoppingCartProps.calculation;
    this.vehicleData = shoppingCartProps.vehicleData;
    this.additionalServices = shoppingCartProps.additionalServices;
    this.durationUnit =
      this.calculation?.durationUnit === DurationUnit.MONTHS
        ? "Monate"
        : this.calculation?.durationUnit;
    this.currency = Output.resolve("generic.currencySymbol");
    this.prefill = shoppingCartProps.prefill;

    this.state = {
      shoppingCartCollapsed: true,
      // No packages included in Content Sheet v8
      includePackages:
        false &&
        this.additionalServices !== undefined &&
        this.additionalServices.servicePackageId !== undefined &&
        this.additionalServices.servicePackageLabel !== undefined,
    };
  }

  renderCollapsedProductView() {
    const model = this.vehicleData.model!;
    return (
      <React.Fragment>
        <div className="c-product-info-summary">
          <span className="c-product-info-summary__img">
            <img src={model.vehicleImageUrl} alt="" />
          </span>
          <span className="c-product-info-summary__heading">
            <span className="c-product-info-summary__title">{model.description}</span>
            <span className="c-product-info-summary__subtitle">
              <i>
                {" "}
                {Output.resolve(
                  `pages.leasing.shoppingCart.underNameText${
                    this.vehicleData.isUsed ? "Used" : "New"
                  }`
                )}
              </i>
            </span>
          </span>
          <span className="c-product-info-summary__info">
            <span className="c-product-info-summary__info-label">
              {this.calculation.duration}{" "}
              {Output.resolve("pages.leasing.shoppingCart.monthlyRates")}
            </span>
            <span className="c-product-info-summary__info-value">
              {formatEuroAmount(this.calculation.monthlyRateAmount)}{" "}
              {this.currency}
            </span>
          </span>
        </div>
      </React.Fragment>
    );
  }

  renderPackage() {
    return (
      <>
        <DescriptionList split>
          <DescriptionList.Group termClassName="c-product-table__summary-term" termText={`${this.calculation.duration} ${Output.resolve("pages.leasing.shoppingCart.monthlyRates")}`} className="u-font-size-fs3">
            <DescriptionList.Detail className="c-product-table__summary-detail">
              {`${formatEuroAmount(this.calculation.monthlyRateAmount)} ${this.currency}`}
            </DescriptionList.Detail>
            <DescriptionList.Detail className="c-product-table__information">
              {Output.resolve("pages.leasing.shoppingCart.brandInfo.offerLease")}
            </DescriptionList.Detail>
          </DescriptionList.Group>
        </DescriptionList>
      </>
    );
  }

  renderEfficiencyClass = (efficiencyClass: string | undefined) => {
    if (efficiencyClass) {
      return (
        <>
          {`${Output.resolve("pages.leasing.shoppingCart.efficiencyClass")}: `}
          {`${efficiencyClass} `}
          <EfficiencyTag rating={efficiencyClass.toLowerCase() as "a" | "b" | "g" | "d" | "a+++" | "a++" | "a+" | "c" | "e" | "f" | undefined} />
          <InfoIcon icon="semantic-info">
            {Output.resolve(
              "pages.leasing.shoppingCart.emissionsTooltip"
            )}
          </InfoIcon>
        </>
      )
    }
  }

  renderImageFooterENVKV = () => {
    return (
      <ENVKVShoppingCartCaption
        onError={() => this.history.push(AppRoute.Error)}
        transactionId={this.prefill?.transaction?.id}
        vehicleData={this.vehicleData}
      />
    )
  }

  renderImageFooter = () => {
    if (isENVKVNew(this.vehicleData)) {
      return this.renderImageFooterENVKV();
    }
    const model = this.vehicleData.model!;
    const economics = model?.economics;
    const wltp = economics?.find((item: any) => item.testProcedureType === TestProcedureType.WLTP);
    const nedc = economics?.find(item => item.testProcedureType === TestProcedureType.NEDC);
    const consumptionsValueWLTP = wltp?.consumptions?.[0]?.combined?.value;
    const consumptionsValueNEDC = economics
      ? nedc?.consumptions?.[0]?.combined?.value
      : model?.consumptionEmission?.nedcConsumptionCombinedAmount;
    const consumptionsUnitWLTP = nedc?.consumptions?.[0]?.combined?.unit
      || Output.resolve("pages.leasing.shoppingCart.imageFooter.defaultConsumptionsUnit");
    const consumptionsUnitNEDC = (economics
      ? nedc?.consumptions?.[0]?.combined?.unit
      : model?.consumptionEmission?.consumptionUnit)
      || Output.resolve("pages.leasing.shoppingCart.imageFooter.defaultConsumptionsUnit");
    const emissionsValueWLTP = wltp?.emissions?.[0]?.combined?.value;
    const emissionsValueNEDC = economics
      ? nedc?.emissions?.[0]?.combined?.value
      : model?.consumptionEmission?.nedcEmissionsCombinedAmount;
    const emissionsUnitWLTP = wltp?.emissions?.[0]?.combined?.unit
      || Output.resolve("pages.leasing.shoppingCart.imageFooter.defaultEmissionsUnit");
    const emissionsUnitNEDC = (economics
      ? nedc?.emissions?.[0]?.combined?.unit
      : model?.consumptionEmission?.emissionsUnit)
      || Output.resolve("pages.leasing.shoppingCart.imageFooter.defaultEmissionsUnit");;
    const efficiencyClass = economics
      ? parseEfficiencyClass(economics?.[0]?.efficiencyClass)
      : model?.consumptionEmission?.efficiencyClass;
    return !this.vehicleData.isUsed && (
      <>
        <Paragraph>
          {Output.resolve(
            "pages.leasing.shoppingCart.imageFooter.WLTP.consumptionCombined"
          )}
          :&nbsp;
          {(notFalsy(consumptionsValueWLTP) ? consumptionsValueWLTP : "-") + " " +
            (consumptionsUnitWLTP || "")}{" "}
          <InfoIcon icon="semantic-info">
            <Paragraph
              dangerouslySetInnerHTML={{ __html: Output.resolve(
                "pages.leasing.shoppingCart.imageFooter.emissionsTooltip") }}
            />
          </InfoIcon>
          <br />
          {Output.resolve("pages.leasing.shoppingCart.imageFooter.WLTP.co2")}:&nbsp;
          {(notFalsy(emissionsValueWLTP) ? emissionsValueWLTP : "-") + " " +
            (emissionsUnitWLTP || "")}{" "}
          <InfoIcon icon="semantic-info">
            <Paragraph
              dangerouslySetInnerHTML={{ __html: Output.resolve(
                "pages.leasing.shoppingCart.imageFooter.emissionsTooltip") }}
            />
          </InfoIcon>
          <br />
        </Paragraph>

        <Paragraph>
          {Output.resolve(
            "pages.leasing.shoppingCart.imageFooter.NEDC.consumptionCombined"
          )}
          :&nbsp;
          {(notFalsy(consumptionsValueNEDC) ? consumptionsValueNEDC : "-") + " " +
            (consumptionsUnitNEDC || "")}{" "}
          <InfoIcon icon="semantic-info">
            <Paragraph
              dangerouslySetInnerHTML={{ __html: Output.resolve(
                "pages.leasing.shoppingCart.imageFooter.emissionsTooltip") }}
            />
          </InfoIcon>
          <br />
          {Output.resolve("pages.leasing.shoppingCart.imageFooter.NEDC.co2")}:&nbsp;
          {(notFalsy(emissionsValueNEDC) ? emissionsValueNEDC : "-") + " " +
            (emissionsUnitNEDC || "")}{" "}
          <InfoIcon icon="semantic-info">
            <Paragraph
              dangerouslySetInnerHTML={{ __html: Output.resolve(
                "pages.leasing.shoppingCart.imageFooter.emissionsTooltip") }}
            />
          </InfoIcon>
          <br />
          {Output.resolve(
            "pages.leasing.shoppingCart.imageFooter.NEDC.energyEfficiency"
          )}
          :&nbsp;
          {(notFalsy(efficiencyClass) ? efficiencyClass : "-")}{" "}
          <InfoIcon icon="semantic-info">
            <Paragraph
              dangerouslySetInnerHTML={{ __html: Output.resolve(
                "pages.leasing.shoppingCart.imageFooter.emissionsTooltip") }}
            />
          </InfoIcon>
        </Paragraph>

        <Paragraph>
          <ResponsiveImage
            src={`${process.env.PUBLIC_URL}/assets/images/Efficiency-List/${
              getEfficiencyClassImage(efficiencyClass)
            }`}
            height="280vw"
          />
        </Paragraph>

        {Output.resolve("pages.leasing.shoppingCart.imageFooter.suffix.texts")?.length > 0 && 
          Output.resolve("pages.leasing.shoppingCart.imageFooter.suffix.texts").map((text: string) => (
            <Paragraph
              dangerouslySetInnerHTML={{ __html: text }}
            />
          ))
        }
      </>
    )
  }

  renderExpandedProductView() {
    const model = this.vehicleData.model!;

    return (
      <React.Fragment>
        <div className="c-product-info">
          <div className="c-product-info__media">
            <header>
              <h3 className="c-product-info__title">{model.description}</h3>
            </header>
            <figure className="c-product-info__figure" role="group">
              <img
                className="c-product-info__image"
                src={model.vehicleImageUrl}
                alt=""
              />
            </figure>
            <br />
            {this.renderImageFooter()}
          </div>
          <ProductTable 
            title={Output.resolve("pages.leasing.shoppingCart.heading")}
            summary={this.renderPackage()}
          >
            <ProductTable.Section>
              <DescriptionList split>
                <DescriptionListGroup termText={Output.resolve("pages.leasing.shoppingCart.duration")} detail={`${this.calculation.duration} ${this.durationUnit}`} />
                <DescriptionListGroup termText={Output.resolve("pages.leasing.shoppingCart.yearlyMileage")} detail={`${formatMileage(this.calculation.yearlyMileage)} km`} />
                <DescriptionListGroup termText={Output.resolve("pages.leasing.shoppingCart.downPaymentAmount")} detail={`${formatEuroAmount(this.calculation.downPaymentAmount)} ${this.currency}`} />
              </DescriptionList>
            </ProductTable.Section>
          </ProductTable>
        </div>
      </React.Fragment>
    );
  }

  renderCollapsibleShoppingCart() {
    return (
      <React.Fragment>
        <div
          className="c-fold-out  js-fold-out js-is-ready"
          id="shopping-cart-fold-out"
        >
          <button
            className="c-fold-out__header  js-fold-out__toggle"
            type="button"
            aria-expanded={!this.state.shoppingCartCollapsed}
            onClick={() => {
              this.setState({
                shoppingCartCollapsed: !this.state.shoppingCartCollapsed,
              });
            }}
          >
            {this.renderCollapsedProductView()}
          </button>
          <div
            className="c-fold-out__content  js-fold-out__content js-is-ready"
            aria-hidden={this.state.shoppingCartCollapsed}
          >
            {this.renderExpandedProductView()}
          </div>
          <button
            className="c-fold-out__footer  js-fold-out__toggle"
            type="button"
            aria-expanded={this.state.shoppingCartCollapsed}
            onClick={() => {
              this.setState({
                shoppingCartCollapsed: !this.state.shoppingCartCollapsed,
              });
            }}
          ></button>
        </div>
      </React.Fragment>
    );
  }

  render() {
    if (!this.vehicleData || !this.calculation) {
      return <></>;
    }

    return (
      <div className="u-mb-large">{this.renderCollapsibleShoppingCart()}</div>
    );
  }
}

type ShoppingCartProps = {
  history: any;
  prefill: any;
  calculation: Calculation;
  vehicleData: VehicleData;
  additionalServices: AdditionalServicesDescriptionItem;
};

type ShoppingCartState = {
  shoppingCartCollapsed: boolean;
  includePackages: boolean;
};

export { ShoppingCart };
